import React, { useMemo } from 'react';
import { format } from 'date-fns';

import { Customer } from '@/@types/customer';
import { Operation } from '@/@types/operation';
import { Output } from '@/@types/outputs';
import { PaymentTerm } from '@/@types/payment-term';
import { Staff } from '@/@types/staff';
import { Vendor } from '@/@types/vendor';
import {
  Header,
  POS_PRINT_FONT_FAMILY,
  POS_PRINT_58MM_FONT_WEIGHT,
  POS_PRINT_58MM_FONT_SIZE,
  POS_PRINT_BOLD_FONT_WEIGHT,
  POS_PRINT_RENDERING_STYLES,
  Separator,
} from '@/components/common/PosPrint';
import { DOCUMENT_TYPE_CORTESIA } from '@/modules/document_types/utils/documentTypes';
import { convertNumberToMoney } from '@/utils';

export type OutputPrint58mmVariant = 'SALE' | 'OTHER_OUTPUT';

const PRINT_FEED_HEIGHT = '2cm';

interface OutputMetadata58mmProps {
  number?: string | number;
  date?: string;
}

interface OutputInfo58mmProps {
  operation?: Operation;
  person?: Customer | Vendor;
  staff?: Staff;
  paymentTerm?: PaymentTerm | null;
  note?: string;
}

interface OutputInfoSimple58mmProps {
  staff?: Staff;
  note?: string;
}

function PrintFeed58mm() {
  return (
    <div style={{ height: PRINT_FEED_HEIGHT, minHeight: PRINT_FEED_HEIGHT }}>
      <p>.</p>
      <p>.</p>
    </div>
  );
}

function formatOutputDate(date?: string) {
  return date ? format(new Date(date), 'dd/MM/yyyy HH:mm') : '';
}

function OutputMetadata58mm({ number, date }: OutputMetadata58mmProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.1cm',
        justifyContent: 'space-between',
        whiteSpace: 'nowrap',
      }}
    >
      <span>#{number}</span>
      <span>{formatOutputDate(date)}</span>
    </div>
  );
}

function OutputInfo58mm({
  operation,
  person,
  staff,
  paymentTerm,
  note,
}: OutputInfo58mmProps) {
  return (
    <div>
      <div>
        Tipo: {operation?.number} - {operation?.name}
      </div>
      <div>
        Cliente: {person?.number} - {person?.name}
      </div>
      <div>End: {person?.streetName || '-'}</div>
      <div>
        {person?.streetNumber
          ? `Nº ${person.streetNumber.padStart(4, '0')}`
          : ''}{' '}
        {person?.neighborhood}
      </div>
      <div>{person?.complement}</div>
      <div>{person?.landmark}</div>
      <div>
        Vendedor: {staff?.number} - {staff?.name}
      </div>
      {paymentTerm ? (
        <div>
          Cond. Pagto: {paymentTerm.number} - {paymentTerm.name}
        </div>
      ) : null}
      {note ? <div>Obs: {note}</div> : null}
    </div>
  );
}

function OutputInfoSimple58mm({ staff, note }: OutputInfoSimple58mmProps) {
  return (
    <div>
      <div>
        Colab: {staff?.number} - {staff?.name}
      </div>
      <div>Obs: {note}</div>
    </div>
  );
}

interface OutputPrint58mmProps {
  output?: Output | null;
  containerRef: React.RefObject<HTMLDivElement>;
  variant: OutputPrint58mmVariant;
}

export function OutputPrint58mm({
  output,
  containerRef,
  variant,
}: OutputPrint58mmProps) {
  const transformedOutputTotal = useMemo(() => {
    const courtesyInstallmentsTotal =
      output?.installments
        ?.filter(
          (installment) =>
            installment?.documentType?.type === DOCUMENT_TYPE_CORTESIA
        )
        .reduce((total, installment) => total + installment.amount, 0) || 0;

    return convertNumberToMoney(
      (output?.total || 0) - courtesyInstallmentsTotal || 0
    );
  }, [output]);

  return (
    <div
      ref={containerRef}
      style={{
        boxSizing: 'border-box',
        color: 'black',
        fontFamily: POS_PRINT_FONT_FAMILY,
        fontSize: POS_PRINT_58MM_FONT_SIZE,
        fontWeight: POS_PRINT_58MM_FONT_WEIGHT,
        padding: '0 0.1cm',
        printColorAdjust: 'economy',
        ...POS_PRINT_RENDERING_STYLES,
        width: '4.8cm',
      }}
    >
      {variant === 'SALE' ? (
        <>
          <Separator edge="start" />
          <Header />
          <Separator />
          <OutputMetadata58mm
            number={output?.number}
            date={output?.issueDate}
          />
          <OutputInfo58mm
            operation={output?.operation}
            person={output?.customer}
            staff={output?.staff}
            paymentTerm={output?.paymentTerm}
            note={output?.note}
          />
          <Separator />
          <div style={{ fontWeight: POS_PRINT_BOLD_FONT_WEIGHT }}>PRODUTOS</div>
          <Separator />
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <tbody>
              {output?.products?.map((product, index) => (
                <React.Fragment key={product.id}>
                  <tr>
                    <td colSpan={5}>{product.outputProduct.name}</td>
                  </tr>
                  <tr
                    style={{
                      borderBottom:
                        index + 1 < output?.products?.length
                          ? '1px solid #333333'
                          : '',
                    }}
                  >
                    <td style={{ textAlign: 'right' }}>
                      {product.productQuantity.toFixed(2)}{' '}
                      {product.outputUnit.abbreviation} x
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {convertNumberToMoney(product?.unitaryValue || 0)} =
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {convertNumberToMoney(product?.subtotalProduct || 0)}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <Separator />
          <table style={{ width: '100%', textAlign: 'right' }}>
            <tbody>
              <tr>
                <td>Total Produtos:</td>
                <td>{convertNumberToMoney(output?.totalGross || 0)}</td>
              </tr>
              <tr>
                <td>Desconto:</td>
                <td>{convertNumberToMoney(output?.totalDiscount || 0)}</td>
              </tr>
              <tr>
                <td>
                  <p
                    style={{
                      fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
                      textTransform: 'uppercase',
                    }}
                  >
                    Total a Pagar:
                  </p>
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
                  }}
                >
                  {transformedOutputTotal}
                </td>
              </tr>
              {output?.installments?.map((installment) => (
                <tr key={installment.id}>
                  <td>{installment?.documentType?.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    {convertNumberToMoney(installment?.amount || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Separator />
          <p
            style={{
              fontWeight: POS_PRINT_BOLD_FONT_WEIGHT,
              textAlign: 'center',
            }}
          >
            NÃO É DOCUMENTO FISCAL
          </p>
          <Separator edge="end" />
        </>
      ) : (
        <>
          <Separator edge="start" />
          <Header />
          <Header
            title={`${output?.operation?.number || ''} - ${
              output?.operation?.name || ''
            }`}
          />
          <Separator />
          <OutputMetadata58mm
            number={output?.number}
            date={output?.issueDate}
          />
          <OutputInfoSimple58mm staff={output?.staff} note={output?.note} />
          <Separator />
          <div style={{ fontWeight: POS_PRINT_BOLD_FONT_WEIGHT }}>PRODUTOS</div>
          <Separator />
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <tbody>
              {output?.products?.map((product, index) => (
                <tr
                  key={product.id}
                  style={{
                    borderBottom:
                      index + 1 < output?.products?.length
                        ? '1px solid #333333'
                        : '',
                  }}
                >
                  <td>{product.outputProduct.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    {product.productQuantity.toFixed(2)}{' '}
                    {product.outputUnit.abbreviation}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Separator edge="end" />
          <br />
          <div
            style={{
              textAlign: 'center',
              marginTop: '16px',
              marginBottom: '24px',
            }}
          >
            <p>________________________________</p>
            <p>{output?.user.name}</p>
          </div>
          <br />
        </>
      )}
      <PrintFeed58mm />
    </div>
  );
}
